// components\missions\all-missions.jsx
import dynamic from 'next/dynamic';
import React, { useEffect } from "react";
import { useState } from "react";
import { Fragment } from "react";
import MessageModal from "@/components/ui/Message-Modal";
import Modal from "@/components/ui/Modal";
import CountdownCustom from "@/utils/countdown-custom";
import { toast } from "react-toastify";
import {
  checkRewardClaimed,
  fetchAllMissions,
  hasClaimedInternalDailyReward,
  checkAndProcessFaucetClaim,
  checkOperationSatoshiLevelRequirement
} from "@/utils/supabaseFunctions";
import { Icon } from "@iconify/react/dist/iconify.js";
import TwitterCard from "@/components/ui/TwitterCard";
import Image from "next/image";
import { generateJWTToken } from "@/utils/helpers/helperFunctions";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/custom/ui/tooltip'

const Button = dynamic(() => import('@/components/ui/Button'));

export default function AllMissions({ user, loading, userHandles, ipAddress, walletAddress, onPointsUpdate }) {
  const [combinedMissions, setCombinedMissions] = useState([]);
  const [socialMission, setSocialMission] = useState({});
  const [innerLoading, setInnerLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [claimedMissionId, setClaimedMissionId] = useState(null);
  const [uid, setUid] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [activeError, setActiveError] = useState(null);
  const [activeErrorTitle, setActiveErrorTitle] = useState(null);
  const [claimedRewardAmount, setClaimedRewardAmount] = useState(0);
  const [isTwitterModalOpen, setIsTwitterModalOpen] = React.useState(false);
  const [allLRCMissionsDone, setAllLRCMissionsDone] = useState(false);
  const [isProcessingClaim, setIsProcessingClaim] = useState(false);

  // Utility functions to reduce repetitive code
  const showError = (message, title = "Error") => {
    setActiveError(message);
    setActiveErrorTitle(title);
    setShowErrorModal(true);
  };

  const resetLoadingStates = () => {
    setClaiming(false);
    setInnerLoading(false);
  };

  const handleClaimSuccess = (missionId, rewardAmount) => {
    setShowSuccessModal(true);
    setClaimed(true);
    setClaimedMissionId(missionId);
    setClaimedRewardAmount(rewardAmount);
    setClaiming(false);
    
    if (onPointsUpdate) {
      onPointsUpdate(rewardAmount);
    }
  };

  const handleClaimError = (mission, errorMessage = "Please complete mission first before claiming.", title = "Mission Not Complete") => {
    watchFailedClaims(mission);
    showError(errorMessage, title);
    resetLoadingStates();
    setClaimed(true);
  };

  // Mission processing utilities
  const processMissions = (missions) => {
    const dailyMissions = missions.filter(mission => mission.frequency === "daily");
    const onceMissions = missions.filter(mission => mission.frequency !== "daily");
    return { dailyMissions, onceMissions };
  };

  const updateMissionsWithClaimStatus = async (missions, uid, isDaily = false) => {
    return await Promise.all(
      missions.map(async (mission) => {
        const hasReward = user
          ? await checkRewardClaimed(uid, mission.mission_id, isDaily)
          : false;
        return { ...mission, claimed: hasReward };
      })
    );
  };

  const sortMissions = (missions) => {
    // Sort by type priority (check_in first, then quest first among others)
    missions.sort((a, b) => {
      if (a.mission_type === "check_in" && b.mission_type !== "check_in") return -1;
      if (a.mission_type !== "check_in" && b.mission_type === "check_in") return 1;
      if (a.mission_type === "quest" && b.mission_type !== "quest") return -1;
      if (a.mission_type !== "quest" && b.mission_type === "quest") return 1;
      return new Date(a.end_time.seconds) - new Date(b.end_time.seconds);
    });

    // Sort by claim status (unclaimed first)
    missions.sort((a, b) => {
      if (a.claimed && !b.claimed) return 1;
      if (!a.claimed && b.claimed) return -1;
      return 0;
    });

    return missions;
  };

  const filterMissions = (missions) => {
    let filteredMissions = missions;

    // Filter out test missions in production
    if (
      process.env.NEXT_PUBLIC_IS_PRODUCTION !== undefined &&
      (process.env.NEXT_PUBLIC_IS_PRODUCTION === true ||
        process.env.NEXT_PUBLIC_IS_PRODUCTION === "true")
    ) {
      filteredMissions = filteredMissions.filter(
        (mission) => !mission.is_test || mission.is_test === false
      );
    }

    // Filter out expired missions
    filteredMissions = filteredMissions.filter((mission) => {
      if (mission.end_time.seconds && mission.frequency !== "daily") {
        return !(
          (new Date().getTime() / 1000).toFixed(0) >
          mission.end_time.seconds.toFixed(0)
        );
      }
      return true;
    });

    return filteredMissions;
  };

  // Mission claim validation handlers
  const handleChainMissionClaim = async (mission) => {
    const result = await checkAndProcessFaucetClaim(
      walletAddress,
      mission,
      handleRewardMXS
    );

    if (result.success) {
      return { success: true };
    } else {
      watchFailedClaims(mission);
      
      const errorMessage = (
        <Fragment>
          <p className="text-sm">
            Please complete the faucet request first before claiming this mission.
          </p>
          <div className="flex justify-center mt-4">
            <a
              href="/faucet"
              target="_blank"
              className="text-blue-400 hover:underline flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              Go to Faucet Page <Icon icon="mdi:arrow-right" className="w-4 h-4" />
            </a>
          </div>
        </Fragment>
      );
      
      return { success: false, error: errorMessage, title: "Mission Not Complete" };
    }
  };

  const handleOperationSatoshiMission = async (mission) => {
    // Check if already claimed
    const alreadyClaimed = await checkRewardClaimed(uid, mission.mission_id);
    
    if (alreadyClaimed) {
      return { success: false, error: "You have already claimed this mission.", title: "Already Claimed" };
    }

    // Check level requirement
    const levelCheckResult = await checkOperationSatoshiLevelRequirement(uid, mission);
    
    if (!levelCheckResult.success) {
      watchFailedClaims(mission);
      
      const errorMessage = (
        <Fragment>
          <p className="text-sm">
            {levelCheckResult.error}
          </p>
          <div className="flex justify-center mt-4">
            <a
              href="/"
              target="_blank"
              className="text-blue-400 hover:underline flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              Play Operation Satoshi <Icon icon="mdi:arrow-right" className="w-4 h-4" />
            </a>
          </div>
        </Fragment>
      );
      
      return { success: false, error: errorMessage, title: "Mission Not Complete" };
    }

    return { success: true };
  };

  const handleGameQuestMission = async (mission) => {
    // Check if already claimed today
    const alreadyClaimed = await checkRewardClaimed(uid, mission.mission_id, true);
    
    if (alreadyClaimed) {
      return { success: false, error: "You have already claimed this mission today.", title: "Already Claimed" };
    }
    
    // Check if internal reward has been claimed
    const internalRewardClaimed = await hasClaimedInternalDailyReward(uid, mission.game_name);
    
    if (!internalRewardClaimed) {
      watchFailedClaims(mission);
      
      const errorMessage = (
        <Fragment>
          <p className="text-sm">
            You need to claim the daily reward in the {mission.game_name.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')} game first.
          </p>
          <div className="flex justify-center mt-4">
            <a
              href={mission.gen_link_url || "https://metaxseed.io/"}
              target="_blank"
              className="text-blue-400 hover:underline flex items-center gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              Go to Games <Icon icon="mdi:arrow-right" className="w-4 h-4" />
            </a>
          </div>
        </Fragment>
      );
      
      return { success: false, error: errorMessage, title: "Mission Not Complete" };
    }

    return { success: true };
  };

  const handleDailyMissionClaim = async (mission) => {
    const status = await checkRewardClaimed(uid, mission.mission_id, true);
    if (status) {
      return { success: false, error: "You have already claimed this mission today.", title: "Already Claimed" };
    }
    return { success: true };
  };

  const handleOnceMissionClaim = async (mission) => {
    const status = await checkRewardClaimed(uid, mission.mission_id);
    if (status) {
      return { success: false, error: "You have already claimed this mission.", title: "Already Claimed" };
    }
    return { success: true };
  };

  // First useEffect for initial load and user changes
  useEffect(() => {
    const loadInitialMissions = async () => {
      setInnerLoading(true);
      try {
        const missions = await fetchAllMissions();
        setUid(user?.uid);
        const uid = user?.uid;
        const { dailyMissions, onceMissions } = processMissions(missions);
        const updatedDailyMissions = await updateMissionsWithClaimStatus(dailyMissions, uid, true);
        const updatedOnceMissions = await updateMissionsWithClaimStatus(onceMissions, uid, false);

        let combinedMissions = [
          ...updatedDailyMissions,
          ...updatedOnceMissions,
        ];

        combinedMissions = sortMissions(combinedMissions);
        combinedMissions = filterMissions(combinedMissions);

        setCombinedMissions(combinedMissions);
        setInnerLoading(false);
      } catch (error) {
        console.error("Error fetching missions:", error);
        setInnerLoading(false);
      }
    };

    loadInitialMissions();
  }, [user]); // Only depend on user changes

  // Second useEffect for handling claim updates
  useEffect(() => {
    const updateClaimedMissions = async () => {
      if (!claimed || !user || !claimedMissionId) return;
      
      setInnerLoading(true);
      try {
        // Instead of rechecking all missions, just update the one that was claimed
        setCombinedMissions(prevMissions => 
          prevMissions.map(mission => 
            mission.mission_id === claimedMissionId 
              ? { ...mission, claimed: true } 
              : mission
          )
        );
        
        setInnerLoading(false);
        setClaimed(false);
        setClaimedMissionId(null);
      } catch (error) {
        console.error("Error updating claimed mission:", error);
        setInnerLoading(false);
      }
    };

    updateClaimedMissions();
  }, [claimed, user, claimedMissionId]); // Depend on claimed, user, and claimedMissionId

  async function handleGeneralWithLink(quest) {
    if (quest?.mission_type === "social" && quest?.link_follow_user) {
      window.open(quest?.link_follow_user, "_blank");
    }
  }

  async function handleSocialWithLink(quest) {
    if (quest?.mission_type === "general" && quest?.gen_link_url) {
      window.open(quest?.gen_link_url, "_blank");
    }
  }

  function checkUserIsBindedToTwitter() {
    if (userHandles && userHandles.length > 0) {
      let found = userHandles.find((handle) => handle.handle_name === "twitter");

      if (found && found.handle_verified) {
        return true;
      } else {
        return false;
      }
    }
  }

  async function handleRewardMXS(mission) {
    // Check if the user is logged in
    if (!user) {
      showError("Connect your wallet to claim rewards.");
      return Promise.reject("User not logged in");
    }

    const url = `https://cqfjxolodeqrlvaifybm.supabase.co/functions/v1/add-daily-reward-website`;
    handleGeneralWithLink(mission);
    handleSocialWithLink(mission);
    
    // Calculate effective reward (double if user has MXS pass)
    const effectiveReward = user.mxs_pass ? mission.reward * 2 : mission.reward;
    setClaimedRewardAmount(effectiveReward);

    try {
      setClaiming(true);
      let data;

      // Handle Twitter LRC option specifically
      if (mission.mission_type === "social" && 
          mission.social && mission.social === "twitter" && 
          mission.twitter_option && mission.twitter_option === "like-retweet-comment") {
        
        if (!checkUserIsBindedToTwitter()) {
          showError(
            "Please connect your Twitter account to claim rewards."
          );
          setClaiming(false);
          return Promise.reject("Twitter account not connected");
        }
        
        setIsTwitterModalOpen(true);
        setSocialMission(mission);
        setClaiming(false);
        return Promise.resolve();
      }

      // Standard data payload for all mission types
      data = {
        uid: uid,
        mission_id: mission.mission_id,
        sub_event: mission.sub_event,
        event: mission.event,
        type: mission.event_type,
        comment: "MISSION SUCCESSFULLY CLAIMED FROM THE WEBSITE.",
        game_name: mission.game_name,
        ip_address: ipAddress,
        mxs_gold_claimed: effectiveReward,
        mission_type: mission.mission_type,
        email: user?.email
      };

      // Log data right before sending the request
      // console.log("Sending reward claim data:", data);

      const token = generateJWTToken();
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const responseData = await response.json();
        // console.log("Reward claim response:", responseData);

        // Display success message
        handleClaimSuccess(mission.mission_id, effectiveReward);
        
        return Promise.resolve();
      } else {
        const error = await response.json();

        watchFailedClaims(mission);

        setClaiming(false);
        handleClaimError(mission);
        return Promise.reject(error);
      }
    } catch (error) {
      setActiveError(error);
      setActiveErrorTitle("Error");
      console.error(error);

      watchFailedClaims(mission);

      setShowErrorModal(true);
      setClaimed(true);

      setClaiming(false);
      return Promise.reject(error);
    }
  }

  function getMissionLatestFailedTime(quest) {
    const failedClaims = getFailedClaims();
    const found = findExistingClaim(failedClaims, quest.mission_id, user?.uid);

    if (found) {
      // Calculate the time when the user can try again (current time + remaining time)
      const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
      const failedTime = Math.floor(found.latest_failed_time / 1000); // Failed time in seconds
      const timeRemaining = 600 - (currentTime - failedTime); // 600 seconds = 10 minutes
      
      // Return the future timestamp when the user can try again
      return currentTime + timeRemaining;
    }
    
    return 0;
  }

  // Failed claims management utilities
  const getFailedClaims = () => {
    const claims = localStorage.getItem("failedClaims");
    return claims ? JSON.parse(claims) : [];
  };

  const setFailedClaims = (claims) => {
    localStorage.setItem("failedClaims", JSON.stringify(claims));
  };

  const findExistingClaim = (claims, questId, userId) => {
    return claims.find(
      (claim) => 
        claim.quest.mission_id === questId && 
        (claim.user.user_id === userId || claim.user.uid === userId)
    );
  };

  const cleanupOldClaims = (claims) => {
    const tenMinutes = 600000;
    const currentTime = new Date().getTime();
    return claims.filter(claim => 
      currentTime - claim.latest_failed_time <= tenMinutes
    );
  };

  function watchFailedClaims(quest) {
    const currentTime = new Date().getTime();
    const tenMinutes = 600000;
    
    const newClaim = {
      quest: quest,
      user: {
        user_id: user?.user_id,
        uid: user?.uid
      },
      latest_failed_time: currentTime,
      count: 1
    };

    const failedClaims = getFailedClaims();
    
    if (failedClaims.length === 0) {
      setFailedClaims([newClaim]);
      return;
    }

    const existingClaim = findExistingClaim(failedClaims, quest.mission_id, user?.uid);

    if (existingClaim) {
      // Update existing claim
      if (currentTime - existingClaim.latest_failed_time > tenMinutes) {
        existingClaim.count = 1; // Reset count if more than 10 minutes have passed
      } else {
        existingClaim.count = (existingClaim.count || 0) + 1; // Increment count if within 10 minutes
      }
      existingClaim.latest_failed_time = currentTime;
    } else {
      failedClaims.push(newClaim);
    }

    // Clean up old claims and save
    const cleanedClaims = cleanupOldClaims(failedClaims);
    setFailedClaims(cleanedClaims);
  }

  function checkFailedClaimsForDisabling(quest) {
    const failedClaims = getFailedClaims();
    const found = findExistingClaim(failedClaims, quest.mission_id, user?.uid);

    if (!found) return true;

    const currentTime = Date.now();
    const timeDiff = currentTime - found.latest_failed_time;
    const tenMinutes = 600000; // 10 minutes in milliseconds

    return !(found.count > 2 && timeDiff < tenMinutes);
  }

  async function checkMissionIsClaimed(mission) {
    if (claiming || isProcessingClaim) return false;
    
    setClaiming(true);
    setInnerLoading(true);

    if (!user) {
      resetLoadingStates();
      return false;
    }

    try {
      // console.log("Checking mission:", {
      //   mission_id: mission.mission_id,
      //   type: mission.mission_type,
      //   frequency: mission.frequency,
      //   game_name: mission.game_name
      // });
      
      let validationResult;

      // Route to appropriate handler based on mission type and frequency
      if (mission?.mission_type === "chain" && mission?.frequency === "daily") {
        validationResult = await handleChainMissionClaim(mission);
      } else if (mission?.frequency === "once" && mission?.game_name === "OPERATION_SATOSHI") {
        validationResult = await handleOperationSatoshiMission(mission);
      } else if (mission?.frequency === "daily" && mission?.mission_type === "quest" && mission?.game_name !== "ECOSYSTEM") {
        validationResult = await handleGameQuestMission(mission);
      } else if (mission?.frequency === "daily") {
        validationResult = await handleDailyMissionClaim(mission);
      } else {
        // console.log("Checking non-daily mission:", mission.mission_id, mission.frequency);
        validationResult = await handleOnceMissionClaim(mission);
      }

      // Handle validation results
      if (!validationResult.success) {
        showError(validationResult.error, validationResult.title);
        resetLoadingStates();
        return false;
      }
      
      // If all checks pass, proceed with claiming
      try {
        await handleRewardMXS(mission);
        setClaimedMissionId(mission.mission_id);
        return true;
      } catch (error) {
        if (error?.msg) {
          handleClaimError(mission);
        }
        resetLoadingStates();
        return false;
      }
    } catch (error) {
      console.error("Error claiming mission:", error);
      showError("An unexpected error occurred. Please try again later.");
      resetLoadingStates();
      return false;
    } finally {
      resetLoadingStates();
    }
  }

  return (
    <Fragment>
      <Fragment>
        {(loading || innerLoading) && (
          <Fragment>
            <div className="mt-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 md:gap-x-8 md:gap-y-12 gap-y-8">
                {[...Array(12)].map((_, i) => (
                  <MissionCard key={i} />
                ))}
              </div>
            </div>
          </Fragment>
        )}
        {!loading && !innerLoading && (
          <Fragment>
            {combinedMissions.length === 0 && (
              <Fragment>
                <div className="flex items-center justify-center py-16 gothamBook">
                  <div className="text-center">
                    <h2 className="text-xl mb-4 font-semibold text-white">
                      No Missions Available
                    </h2>
                    <p className="text-metaxseed-gray">
                      There are no missions available at the moment. Please
                      check back later.
                    </p>
                  </div>
                </div>
              </Fragment>
            )}
            {combinedMissions.length > 0 &&
              combinedMissions.filter((item) => item.claimed).length ===
              combinedMissions.length && (
                <Fragment>
                  <div className="flex items-center justify-center py-16 gothamBook">
                    <div className="text-center">
                      <Image
                        src="https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_nodes%2Fnode_chest.png?alt=media&token=f064df08-466f-438a-b1df-e0ffdf519a30&w=384&q=75"
                        width={285}
                        height={285}
                        alt="MXS GOLD"
                        className="mb-4 max-h-[100px] xl:max-h-[200px] !max-w-fit mx-auto"
                      />
                      <h2 className="text-xl mb-4 font-semibold text-white">
                        Congratulations
                      </h2>
                      <p className="text-metaxseed-gray">
                        You have completed all the missions.
                        <br />
                        Come back tomorrow for even more missions and
                        <br />
                        opportunities to earn GOLD.
                      </p>
                    </div>
                  </div>
                </Fragment>
              )}
            {combinedMissions.length > 0 && (
              <Fragment>
                <div className="mt-8">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 md:gap-x-8 md:gap-y-12 gap-y-8">
                    {combinedMissions
                      .filter((task) => !task.claimed)
                      .map((mission, i) => (
                        <MissionCard
                          user={user}
                          key={i}
                          mission={mission}
                          claimed={mission?.claimed}
                          claiming={claiming}
                          isProcessingClaim={isProcessingClaim}
                          getMissionLatestFailedTime={
                            getMissionLatestFailedTime
                          }
                          onClick={() => {
                            // Prevent multiple clicks while processing
                            if (isProcessingClaim || claiming || !user || mission?.claimed) {
                              return;
                            }

                            if (checkFailedClaimsForDisabling(mission) === false) {
                              showError(
                                "You have failed to claim this mission 3 times please try again later.",
                                "Limit Reached"
                              );
                              return;
                            }

                            setIsProcessingClaim(true);
                            checkMissionIsClaimed(mission).finally(() => {
                              setIsProcessingClaim(false);
                            });
                          }}
                          checkFailedClaimsForDisabling={
                            checkFailedClaimsForDisabling
                          }
                        />
                      ))}
                  </div>
                </div>
              </Fragment>
            )}
          </Fragment>
        )}
      </Fragment>

      {isTwitterModalOpen && (
        <Modal
          title={`${socialMission?.title}`}
          centered
          activeModal={isTwitterModalOpen}
          themeClass="hidden"
          footerClass="hidden"
          onClose={() => {
            setIsTwitterModalOpen(false);
            setClaiming(false);
          }}
          footerContent={<Fragment></Fragment>}
        >
          <div className="flex justify-center items-center">
            <TwitterCard
              mission={socialMission}
              setAllLRCMissionsDone={setAllLRCMissionsDone}
              onClose={() => {
                setIsTwitterModalOpen(false);
                setClaiming(false);
              }}
            />
          </div>
        </Modal>
      )}

      {showSuccessModal && (
        <MessageModal
          title={`Success!`}
          fireworks={true}
          status={true}
          centered
          activeModal={showSuccessModal}
          className="max-w-xl successModalShadow"
          onClose={async () => {
            setShowSuccessModal(false);
            setClaimedRewardAmount(0);
            setClaimed(false);
            setClaiming(false);
          }}
        >
          <Fragment>
            <div className="flex items-center">
              <Image
                src={
                  "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_nodes%2Fnode_chest.png?alt=media&token=f064df08-466f-438a-b1df-e0ffdf519a30&w=384&q=75"
                }
                alt="MXS GOLD"
                width={285}
                height={285}
                className="mb-4 max-h-[100px] xl:max-h-[200px] !max-w-fit mx-auto"
              />
            </div>
            <div className="font-medium py-4 text-center">
              {claimedRewardAmount !== 0 ? (
                <span className="font-bold">
                  {claimedRewardAmount} MXS GOLD
                </span>
              ) : (
                "Reward"
              )}{" "}
              claimed successfully!
            </div>
          </Fragment>
        </MessageModal>
      )}

      {showErrorModal && (
        <MessageModal
          title={activeErrorTitle || "Error"}
          status={false}
          centered
          activeModal={showErrorModal}
          className="max-w-xl errorModalShadow"
          onClose={(event) => {
            if (event) {
              setShowErrorModal(false);
              setTimeout(async () => {
                setActiveError(null);
                setActiveErrorTitle(null);
                setClaiming(false);
                setInnerLoading(false);
              }, 300);
            }
          }}
        >
          <div className="font-medium py-2 text-center">
            {activeError || "An error occurred. Please try again later."}
          </div>
        </MessageModal>
      )}
    </Fragment>
  );
}

const MissionCard = ({
  mission,
  user,
  claiming,
  isProcessingClaim,
  onClick,
  checkFailedClaimsForDisabling,
  getMissionLatestFailedTime,
}) => {
  // Calculate effective reward display (double if user has MXS pass)
  const effectiveReward = user?.mxs_pass ? mission?.reward * 2 : mission?.reward;
  
  return (
    <Fragment>
      {!mission && (
        <div className="mission-card deactivated rounded-xl animate-pulse duration-75 opacity-35">
          <div className="mission-card-category min-w-12 animate-pulse duration-75">
            <div className="w-full h-4 bg-metaxseed-gray"></div>
          </div>
          <div className="mission-card-countdown animate-pulse duration-75">
            <div className="w-full h-4 bg-metaxseed-gray"></div>
          </div>
          <div className="mission-card-content">
            <div className="mission-card-header">
              <div className="mission-card-frequency min-w-12 animate-pulse duration-75">
                <div className="w-full h-4 bg-metaxseed-gray"></div>
              </div>
            </div>
            <div className="mission-card-image">
              <Image
                src={
                  "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_missions%2Ficons%2Ficon_200_cup.png?alt=media&token=bf1d142b-699e-4abc-9fbc-bdb521f08df3"
                }
                className="h-full object-contain animate-pulse duration-75"
                width={200}
                height={200}
                alt="Mission"
              />
            </div>
            <div className="px-8 mb-6 -mt-2">
              <div className="w-full h-4 bg-metaxseed-gray animate-pulse duration-75 rounded-xl"></div>
            </div>
            <div className="px-8 leading-none mb-2">
              <div className="w-full h-4 bg-metaxseed-gray animate-pulse duration-75 rounded-xl"></div>
            </div>
            <div className="px-8 leading-none mb-2">
              <div className="w-full h-4 bg-metaxseed-gray animate-pulse duration-75 rounded-xl"></div>
            </div>
            <div className="px-8 leading-none">
              <div className="w-full h-4 bg-metaxseed-gray animate-pulse duration-75 rounded-xl"></div>
            </div>
          </div>
        </div>
      )}
      {mission && (
        <div
          className={
            "mission-card rounded-xl" +
            " " +
            (mission?.mission_type || "general") +
            (mission?.frequency === "daily" ? " daily" : " not-daily") +
            (mission?.claimed ? " claimed" : "") +
            (checkFailedClaimsForDisabling(mission) ? "" : " timeout")
          }
          onClick={onClick}
        >
          <div className="mission-card-bg-pattern"></div>
          <div className="mission-card-category">
            {mission?.mission_type.replace("_", " ") || "General"}
          </div>
          <div className="mission-card-countdown">
            <CountdownCustom
              date={mission?.frequency === "daily" ? null : mission?.end_time}
            />
          </div>
          <div className="mission-card-content">
            <div className="flex flex-row items-center gothamMedium mission-card-reward">
              <Image
                src="https://res.cloudinary.com/dot2onqdb/image/upload/v1729068489/gold_lr_h4yg2m.png"
                className="w-6 h-6 mr-1"
                alt="MXS GOLD"
                width={200}
                height={200}
              />
              <div className="flex flex-1 flex-row items-center">
                <span className="text-lg font-bold text-white">
                  {mission?.reward}
                </span>
                {user?.mxs_pass && (
                  <TooltipProvider>
                    <Tooltip delayDuration={100}>
                      <TooltipTrigger asChild>
                        <div className="ml-2 relative cursor-pointer">
                          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full p-0.5 shadow-lg shadow-yellow-500/20">
                            <div className="bg-metaxseed-black rounded-full px-2 py-1 relative flex items-center gap-1">
                              <Icon
                                icon="mdi:crown"
                                className="text-yellow-500 w-3 h-3"
                              />
                              <span className="text-yellow-500 text-xs font-bold">2×</span>
                            </div>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-metaxseed-black border border-orange-400 shadow-xl p-3 max-w-xs animate-in fade-in-75 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-75 data-[state=closed]:zoom-out-95 duration-200">
                        <div className="flex items-center gap-1 mb-2">
                          <Icon icon="mdi:crown" className="h-4 w-4 text-yellow-500" />
                          <p className="text-yellow-500 font-medium">MXS Pass Bonus</p>
                        </div>
                        <p className="text-sm text-zinc-200">
                          You're receiving double rewards for this mission because you have an active MXS Pass subscription!
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
            <div className="mission-card-header">
              {/*
            <div className="mission-card-gold">{mission?.reward} GOLD</div>
           */}
              <div className="mission-card-frequency">{mission?.frequency}</div>
            </div>
            <div className="mission-card-image">
              <Image
                src={
                  mission?.mission_img
                    ? mission?.mission_img
                    : "https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_missions%2Ficons%2Ficon_200_cup.png?alt=media&token=bf1d142b-699e-4abc-9fbc-bdb521f08df3"
                }
                className="h-full object-contain"
                width={200}
                height={200}
                alt="Mission"
              />
            </div>
            {mission?.title && (
              <div className="px-8 mb-2 -mt-2">
                <span className="text-sm gothamMedium drop-shadow-xl">
                  {mission?.title}
                </span>
              </div>
            )}
            {mission?.brief && (
              <div className="px-8 leading-none mb-2">
                <span className="text-xs gothamBook leading-none text-metaxseed-gray italic drop-shadow-xl">
                  {mission?.brief}
                </span>
              </div>
            )}
            {/* {mission?.is_recurring && (
              <div className="absolute left-4 bottom-4">
                <Icon
                  icon="fa:refresh"
                  className="w-4 h-4 text-metaxseed-text-gray opacity-50"
                />
              </div>
            )} */}

            <div className="absolute left-5 bottom-4 min-h-4 flex items-center">
              <Button
                className="p-0 opacity-50 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();

                  navigator.clipboard.writeText(mission?.mission_id);
                  toast.success("Mission ID copied to clipboard.");
                }}
              >
                <Icon
                  icon="fa:copy"
                  className="w-4 h-4 text-metaxseed-text-gray"
                />

                <small className="hidden">#{mission?.mission_id.slice(0, 3)}...</small>
              </Button>
            </div>

            <div className="mission-card-button"> 
              <div className="mission-card-button-icon text-xs">
                <Image
                  width={200}
                  height={200}
                  alt="Present"
                  style={{ maxWidth: "unset" }}
                  src="https://firebasestorage.googleapis.com/v0/b/prodmxs.appspot.com/o/website%2Fpage_missions%2Ficons%2Ficon_200_present.png?alt=media&token=fec102af-714e-451a-bfd2-3a11f4f84665"
                  className="w-6 h-6"
                />
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClick();
                }}
                disabled={mission?.claimed || claiming || isProcessingClaim}
                className="mission-card-button-label text-xs"
              >
                {user ? (
                  mission?.claimed ? (
                    "Already Claimed"
                  ) : !checkFailedClaimsForDisabling(mission) ? (
                    <Fragment>
                      Try Again In{" "}
                      <CountdownCustom
                        showDay={false}
                        date={getMissionLatestFailedTime(mission)}
                        setUTCMidnight={false}
                        showHours={true}
                      />
                    </Fragment>
                  ) : (
                    "Claim Gold"
                  )
                ) : (
                  "Connect to Claim"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
